import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Laptop, Trash2, Monitor, Smartphone } from 'lucide-react'
import { licenseAPI } from '../services/api'
import type { Device } from '../types'
import toast from 'react-hot-toast'

const Devices: React.FC = () => {
  const { t } = useTranslation()
  const [devices, setDevices] = useState<Device[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchDevices()
  }, [])

  const fetchDevices = async () => {
    try {
      console.log('Fetching devices from licenses...')
      
      // Get licenses first
      const licensesResponse = await licenseAPI.getMyLicenses()
      console.log('Licenses response:', licensesResponse)
      
      const licensesData = licensesResponse.data as any
      let licenses = []
      
      if (Array.isArray(licensesData)) {
        licenses = licensesData
      } else if (licensesData && licensesData.licenses) {
        licenses = licensesData.licenses
      } else if (licensesData && licensesData.history) {
        licenses = licensesData.history
      }
      
      console.log('Found licenses:', licenses)
      
      // Get devices for each license
      const allDevices = []
      for (const license of licenses) {
        try {
          console.log(`Fetching devices for license ${license.id}`)
          const devicesResponse = await licenseAPI.getLicenseDevices(license.id)
          console.log(`Devices for license ${license.id}:`, devicesResponse.data)
          
          if (devicesResponse.data && devicesResponse.data.devices) {
            const devicesWithLicense = devicesResponse.data.devices.map((device: any) => ({
              ...device,
              license_key: license.license_key,
              license_name: license.plan_name
            }))
            allDevices.push(...devicesWithLicense)
          }
        } catch (error) {
          console.error(`Error fetching devices for license ${license.id}:`, error)
        }
      }
      
      console.log('All devices:', allDevices)
      setDevices(allDevices)
    } catch (error) {
      console.error('Error fetching devices:', error)
      setDevices([])
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveDevice = async (deviceId: number) => {
    if (!confirm('Czy na pewno chcesz dezaktywować to urządzenie?')) {
      return
    }

    try {
      await licenseAPI.deactivateDevice(deviceId)
      toast.success('Urządzenie zostało dezaktywowane')
      fetchDevices()
    } catch (error: any) {
      console.error('Remove device error:', error)
      toast.error(error.response?.data?.message || 'Błąd dezaktywacji urządzenia')
    }
  }

  const getDeviceIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'windows':
      case 'mac':
      case 'linux':
        return Monitor
      case 'mobile':
      case 'android':
      case 'ios':
        return Smartphone
      default:
        return Laptop
    }
  }

  const formatLastActive = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Teraz'
    if (diffInMinutes < 60) return `${diffInMinutes} min temu`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} godz temu`
    return `${Math.floor(diffInMinutes / 1440)} dni temu`
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{t('devices.title')}</h1>
        <p className="text-gray-400">{t('devices.subtitle')}</p>
      </div>

      <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Laptop className="h-5 w-5 text-primary mr-2" />
          {t('devices.yourDevices')}
        </h2>

        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse bg-dark-700 h-20 rounded"></div>
            ))}
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12">
            <Laptop className="h-12 w-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">{t('devices.noDevices')}</p>
            <p className="text-sm text-gray-500 mt-2">
              {t('devices.noDevicesDescription')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('devices.deviceName')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('devices.license')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('devices.platform')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('devices.lastSeen')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('devices.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => {
                  const DeviceIcon = getDeviceIcon(device.device_type)
                  
                  return (
                    <tr key={device.id} className="border-b border-dark-700 hover:bg-dark-700/50">
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <DeviceIcon className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{device.device_name}</p>
                            <p className="text-sm text-gray-400 font-mono">{device.device_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-white">{device.license_name}</p>
                          <p className="text-sm text-gray-400 font-mono">{device.license_key}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className="capitalize text-gray-300">{device.device_type}</span>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-gray-300">{formatLastActive(device.last_active)}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(device.last_active).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${
                          device.is_active 
                            ? 'bg-green-900/20 text-green-400 border-green-900/50' 
                            : 'bg-gray-900/20 text-gray-400 border-gray-900/50'
                        }`}>
                          {device.is_active ? t('devices.active') : t('devices.inactive')}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <button
                          onClick={() => handleRemoveDevice(device.id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-600/10 rounded-lg transition-colors"
                          title={t('devices.remove')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info box */}
      <div className="bg-blue-900/20 border border-blue-900/50 rounded-lg p-4">
        <h3 className="text-blue-400 font-medium mb-2">{t('devices.deviceInfo')}</h3>
        <p className="text-sm text-blue-300">
          {t('devices.deviceInfoDescription')}
        </p>
      </div>
    </div>
  )
}

export default Devices